import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pill, EmptyState, StatCard, ExportButton } from "./AdminUI";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

const STATUSES = ["new", "reviewing", "planned", "resolved", "closed", "spam_review"];
const TYPES = ["bug", "feature_request", "usability", "compliment", "content_or_category", "safety_or_trust", "other", "access_help"];
const AREAS = ["signup_login", "profile", "search_discovery", "my_board", "feed", "wall", "messaging", "hire_requests", "reviews", "verification", "billing_subscription", "feedback", "accessibility", "other"];
const IMPACTS = ["blocked", "major_difficulty", "minor_difficulty", "suggestion_only", "positive"];
const label = (s) => (s || "unknown").replaceAll("_", " ");

export default function FeedbackTab() {
  const [metrics, setMetrics] = useState(null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ source: "", status: "", feedback_type: "", feature_area: "", impact: "", flagged: false });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/feedback/admin/list", {
    params: { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), search: search || undefined, sort },
  }).then(r => setData(r.data));
  useEffect(() => { load(); }, [filters, search, sort]); // eslint-disable-line
  useEffect(() => { api.get("/feedback/admin/metrics").then(r => setMetrics(r.data)); }, [data]);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  if (!data || !metrics) return <p className="text-sm text-[#222]/45">Loading…</p>;
  const { items, counts } = data;

  return (
    <div data-testid="admin-feedback-tab" className="space-y-6">
      <div className="flex justify-end"><ExportButton kind="feedback" label="Feedback & support" /></div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Total feedback" value={metrics.total} testid="feedback-stat-total" />
        <StatCard label="New" value={metrics.new_vs_resolved.new} testid="feedback-stat-new" />
        <StatCard label="Resolved/closed" value={metrics.new_vs_resolved.resolved} testid="feedback-stat-resolved" />
        <StatCard label="Avg. hrs to first review" value={metrics.avg_hours_to_first_review ?? "—"} testid="feedback-stat-avg-review" />
        <StatCard label="Spam review volume" value={metrics.spam_review_volume} testid="feedback-stat-spam" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <ChartBlock title="Feedback by type" rows={metrics.by_type.map(r => ({ name: label(r.name), value: r.value }))} color="#2E6B57" />
        <ChartBlock title="Feedback by feature area" rows={metrics.by_feature_area.map(r => ({ name: label(r.name), value: r.value }))} color="#3B82F6" />
        <ChartBlock title="Most-reported bugs (by area)" rows={metrics.most_reported_bugs.map(r => ({ name: label(r.name), value: r.value }))} color="#EF4444" />
        <ChartBlock title="Most-requested features (by area)" rows={metrics.most_requested_features.map(r => ({ name: label(r.name), value: r.value }))} color="#F59E0B" />
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
        <h3 className="text-sm font-bold mb-3">Feedback trend (daily)</h3>
        {metrics.trend.length === 0 ? <p className="text-sm text-[#222]/40 py-8 text-center">No feedback yet</p> : (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={metrics.trend}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#2E6B57" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search subject, details, reference #, name, email…"
          className="text-xs px-3 py-2 border border-black/10 rounded-full flex-1 min-w-[220px]" data-testid="feedback-search-input" />
        <select value={filters.source} onChange={e => setF("source", e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="feedback-source-filter">
          <option value="">All sources</option>
          <option value="authenticated_feedback">Authenticated</option>
          <option value="guest_access_help">Guest access help</option>
        </select>
        <select value={filters.status} onChange={e => setF("status", e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="feedback-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select value={filters.feedback_type} onChange={e => setF("feedback_type", e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="feedback-type-filter">
          <option value="">All types</option>
          {TYPES.map(s => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select value={filters.feature_area} onChange={e => setF("feature_area", e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="feedback-area-filter">
          <option value="">All feature areas</option>
          {AREAS.map(s => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select value={filters.impact} onChange={e => setF("impact", e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="feedback-impact-filter">
          <option value="">All impact</option>
          {IMPACTS.map(s => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="feedback-sort-select">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="impact_high">Highest impact</option>
          <option value="impact_low">Lowest impact</option>
          <option value="risk_high">Highest spam risk</option>
          <option value="risk_low">Lowest spam risk</option>
        </select>
        <button onClick={() => setF("flagged", !filters.flagged)} className={`text-xs px-3 py-2 rounded-full font-semibold ${filters.flagged ? "bg-amber-100 text-amber-800" : "bg-black/5"}`} data-testid="feedback-flagged-filter">
          Spam-flagged only
        </button>
      </div>

      <div className="flex gap-2 text-xs text-[#222]/50">
        <span>Authenticated: <b>{counts.authenticated_feedback}</b></span>
        <span>·</span>
        <span>Guest access help: <b>{counts.guest_access_help}</b></span>
        <span>·</span>
        <span>Spam review: <b>{counts.spam_review}</b></span>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No feedback matches" body="Try a different filter or search term." testid="admin-feedback-empty" />
      ) : (
        <div className="space-y-3">
          {items.map(f => (
            <FeedbackRow key={f.id} f={f} expanded={expanded === f.id} onExpand={() => setExpanded(expanded === f.id ? null : f.id)} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ChartBlock({ title, rows, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
      <h3 className="text-sm font-bold mb-3">{title}</h3>
      {(!rows || rows.length === 0) ? <p className="text-sm text-[#222]/40 py-8 text-center">No data yet</p> : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={rows.slice(0, 8)} layout="vertical" margin={{ left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 9 }} />
            <Tooltip />
            <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function FeedbackRow({ f, expanded, onExpand, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [owner, setOwner] = useState(f.assigned_owner || "");

  useEffect(() => {
    if (expanded) api.get(`/feedback/admin/${f.id}`).then(r => setDetail(r.data));
  }, [expanded]); // eslint-disable-line

  const setStatus = async (status) => {
    try { await api.patch(`/feedback/admin/${f.id}/status`, { status }); toast.success("Updated"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveOwner = async () => {
    try { await api.patch(`/feedback/admin/${f.id}/assign`, { owner_email: owner || null }); toast.success("Assigned"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/feedback/admin/${f.id}/note`, { text: note.trim() }); setNote(""); toast.success("Note added"); setDetail(d => ({ ...d })); const r = await api.get(`/feedback/admin/${f.id}`); setDetail(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5" data-testid={`feedback-row-${f.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold flex items-center gap-2 flex-wrap">
            {f.subject}
            <span className="text-[10px] font-mono text-[#222]/40">{f.ref_id}</span>
            <Pill tone={f.source === "guest_access_help" ? "amber" : "blue"}>{f.source === "guest_access_help" ? "Guest" : "Authenticated"}</Pill>
            <Pill tone={f.status === "resolved" || f.status === "closed" ? "green" : f.status === "spam_review" ? "red" : "neutral"}>{label(f.status)}</Pill>
            {f.impact && <Pill tone="neutral">{label(f.impact)}</Pill>}
            {f.risk_score > 0 && <Pill tone="red">Risk {f.risk_score}</Pill>}
          </p>
          <p className="text-xs text-[#222]/50 mt-0.5">
            {label(f.feedback_type)} · {label(f.feature_area)} · {f.email} {f.subscription_tier ? `· ${f.subscription_tier}` : ""} · {new Date(f.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap shrink-0">
          <select value={f.status} onChange={e => setStatus(e.target.value)} className="text-xs px-2 py-1 border border-black/10 rounded-full" data-testid={`feedback-status-select-${f.id}`}>
            {STATUSES.map(s => <option key={s} value={s}>{label(s)}</option>)}
          </select>
          <button onClick={onExpand} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold" data-testid={`feedback-expand-${f.id}`}>
            {expanded ? "Hide" : "Details"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 border-t border-black/5 pt-3 space-y-3 text-xs">
          {!detail ? <p className="text-[#222]/40">Loading…</p> : (
            <>
              <p className="text-[#222]/70">{detail.message}</p>
              {detail.user_goal && <p className="text-[#222]/50">Goal: {detail.user_goal}</p>}
              {detail.feedback_type === "bug" && (
                <div className="bg-black/[0.03] rounded-lg p-3 space-y-1">
                  <p><b>What happened:</b> {detail.bug_what_happened || "—"}</p>
                  <p><b>Expected:</b> {detail.bug_expected || "—"}</p>
                  <p><b>Steps:</b> {detail.bug_steps || "—"}</p>
                  <p><b>Always happens:</b> {detail.bug_always_happens || "—"}</p>
                </div>
              )}
              {detail.attachment_url && <a href={detail.attachment_url} target="_blank" rel="noreferrer" className="text-[#245544] underline" data-testid={`feedback-attachment-${f.id}`}>View attachment</a>}

              <div className="grid sm:grid-cols-2 gap-1 text-[#222]/60">
                <span>User ID: {detail.user_id || "—"}</span>
                <span>Role: {detail.role || "—"}</span>
                <span>Membership tier: {detail.subscription_tier || "—"}</span>
                <span>Account age (days): {detail.account_age_days ?? "—"}</span>
                <span>Email verified: {String(detail.email_verified)}</span>
                <span>Page/route: {detail.page_route || "—"}</span>
                <span>Submitted: {new Date(detail.created_at).toLocaleString()}</span>
                <span>Browser/device: {detail.browser_device || "—"}</span>
                <span>App version: {detail.app_version || "—"} ({detail.environment})</span>
                <span>May contact: {String(detail.follow_up_ok)}</span>
              </div>

              <div className="bg-black/[0.03] rounded-lg p-3">
                <p className="font-semibold mb-1">Spam signals — risk score {detail.risk_score}</p>
                <div className="grid sm:grid-cols-2 gap-1">
                  {Object.entries(detail.spam_flags || {}).map(([k, v]) => (
                    <span key={k} className={v ? "text-red-600" : ""}>{label(k)}: {String(v)}</span>
                  ))}
                  <span>Duplicate count: {detail.duplicate_count}</span>
                  <span>IP: {detail.ip_address}</span>
                </div>
                {detail.duplicate_of_detail && <p className="mt-1">Duplicate of: {detail.duplicate_of_detail.ref_id} — {detail.duplicate_of_detail.subject}</p>}
                {detail.duplicates?.length > 0 && <p className="mt-1">{detail.duplicates.length} later submission(s) marked as duplicates of this one.</p>}
              </div>

              <div>
                <p className="font-semibold mb-1">Status history</p>
                {(detail.status_history || []).map((h, i) => (
                  <p key={i} className="text-[#222]/50">{h.from || "—"} → {label(h.to)} by {h.by} at {new Date(h.at).toLocaleString()}</p>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <input value={owner} onChange={e => setOwner(e.target.value)} placeholder="Assign owner (admin email)" className="flex-1 text-xs px-3 py-1.5 border border-black/10 rounded-full" data-testid={`feedback-owner-input-${f.id}`} />
                <button onClick={saveOwner} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold" data-testid={`feedback-assign-${f.id}`}>Assign</button>
              </div>

              <div>
                <p className="font-semibold mb-1">Internal notes</p>
                {(detail.internal_notes || []).map((n, i) => <p key={i} className="text-[#222]/60">{n.text} — {n.by} ({new Date(n.at).toLocaleDateString()})</p>)}
                <div className="flex items-center gap-2 mt-1">
                  <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add an internal note…" className="flex-1 text-xs px-3 py-1.5 border border-black/10 rounded-full" data-testid={`feedback-note-input-${f.id}`} />
                  <button onClick={addNote} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold" data-testid={`feedback-add-note-${f.id}`}>Add</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
